import { useState, useMemo, type ChangeEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowLeft, Send, User, Mail, Store, Check } from "lucide-react";
import { loadSupabase } from "@/lib/supabase-lazy";
import { trackEvent } from "@/lib/analytics";
import { VenueLegalText } from "@/components/legal/VenueLegalText";
import { venueLegalHref } from "@/lib/venue-legal";

// Cierre del hub — bloque "Recupera tu fin de semana". Header con título
// + subtítulo spanning ambas columnas, composición ilustrada a la izquierda
// y form de newsletter / waitlist (modo stepper, un campo por paso) a la
// derecha. Persiste a `landing_subscriptions`.
//
// El nombre del archivo (`DemoForm.tsx`) se conserva por compatibilidad
// de imports histórica.

type StepKey = "name" | "email" | "local";

interface StepMeta {
  key: StepKey;
  icon: ReactNode;
  i18nKey: "name" | "email" | "local";
  inputType: "text" | "email";
  autoComplete: string;
  maxLength: number;
  /** Validador por paso. Si retorna `false`, el botón "Continuar" no avanza. */
  isValid: (value: string) => boolean;
}

// Regex laxo para validar email — sólo bloquea valores claramente inválidos.
// La validación dura la hace Supabase + el backend.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEPS: StepMeta[] = [
  {
    key: "name",
    icon: <User aria-hidden="true" />,
    i18nKey: "name",
    inputType: "text",
    autoComplete: "name",
    maxLength: 100,
    isValid: (v) => v.trim().length >= 2,
  },
  {
    key: "email",
    icon: <Mail aria-hidden="true" />,
    i18nKey: "email",
    inputType: "email",
    autoComplete: "email",
    maxLength: 255,
    isValid: (v) => EMAIL_RE.test(v.trim()),
  },
  {
    key: "local",
    icon: <Store aria-hidden="true" />,
    i18nKey: "local",
    inputType: "text",
    autoComplete: "organization",
    maxLength: 150,
    isValid: (v) => v.trim().length >= 2,
  },
];

export default function DemoForm() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const [values, setValues] = useState<Record<StepKey, string>>({
    name: "",
    email: "",
    local: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  // LDT-706 · Este formulario es para recibir novedades por email: eso es
  // comunicación comercial (LSSI art. 21), que exige un consentimiento
  // expreso, así que va en una casilla propia y SIN marcar, separada del aviso
  // de privacidad (que es información, no se acepta). Sin ella no se envía:
  // es lo único que el formulario pide. No deja fila en legal_acceptances (no
  // hay local ni perfil); el alta en landing_subscriptions es la constancia.
  const [marketingConsent, setMarketingConsent] = useState(false);

  const currentStep = STEPS[step];
  const currentValue = values[currentStep.key];
  const isLastStep = step === STEPS.length - 1;
  const canProceed = useMemo(
    () => currentStep.isValid(currentValue) && (!isLastStep || marketingConsent),
    [currentStep, currentValue, isLastStep, marketingConsent],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValues((prev) => ({ ...prev, [currentStep.key]: e.target.value }));
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const advance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canProceed) return;

    if (!isLastStep) {
      setStep((s) => s + 1);
      return;
    }

    // Último paso → submit a Supabase.
    setSending(true);
    // LDT-718: Supabase se carga al enviar, no con la landing.
    const supabase = await loadSupabase();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await supabase.from("landing_subscriptions" as any).insert({
      name: values.name.trim(),
      email: values.email.trim(),
      local_name: values.local.trim(),
      language: i18n.language,
    });
    setSending(false);
    // LDT-718: los avisos (sonner) tampoco viajan con la portada.
    const { toast } = await import("@/components/ui/sonner");
    if (error) {
      toast.error(t("subscribe.errorMsg"));
      return;
    }
    trackEvent("form_submitted", { form: "subscribe" });
    toast.success(t("subscribe.success"));
    setDone(true);
  };

  return (
    <section id="subscribe" className="playium-weekend">
      <div className="playium-weekend__inner">
        {/* Cabecera spanning ambas columnas. */}
        <div className="playium-weekend__header">
          <h2 className="playium-weekend__title playium-heading">
            {t("closing.title")}
          </h2>
          <p className="playium-weekend__subtitle">{t("closing.subtitle")}</p>
        </div>

        {/* Panel izquierdo: composición ilustrada. */}
        <div className="playium-weekend__copy">
          <div className="playium-weekend__composition" aria-hidden="true">
            <img loading="lazy"
              src="/images/weekend/FONDO.webp"
              alt=""
              className="playium-weekend__bg"
            />
            <img loading="lazy"
              src="/images/weekend/CALENDARIO.webp"
              alt=""
              className="playium-weekend__layer playium-weekend__layer--calendar"
            />
            <img loading="lazy"
              src="/images/weekend/CHICO_PUF.webp"
              alt=""
              className="playium-weekend__layer playium-weekend__layer--guy"
            />
            <img loading="lazy"
              src="/images/weekend/BOCADILLO.webp"
              alt=""
              className="playium-weekend__layer playium-weekend__layer--bubble"
            />
            <img loading="lazy"
              src="/images/weekend/DATOS.webp"
              alt=""
              className="playium-weekend__layer playium-weekend__layer--stats"
            />
          </div>
        </div>

        {/* Panel derecho: form stepper. Un campo a la vez con transición
            entre pasos. Step indicator arriba (3 dots), back arrow para
            volver, botón principal que avanza o envía. */}
        <div className="playium-weekend__form-card">
          <div className="playium-weekend__form-head">
            <span className="playium-weekend__form-badge" aria-hidden="true">
              <Send />
            </span>
            <div>
              <h3 className="playium-weekend__form-title playium-heading">
                {t("subscribe.title")}
              </h3>
              <p className="playium-weekend__form-subtitle">
                {t("subscribe.subtitle")}
              </p>
            </div>
          </div>

          {done ? (
            <div className="playium-weekend__success" role="status">
              <span
                className="playium-weekend__success-badge"
                aria-hidden="true"
              >
                <Check />
              </span>
              <p className="playium-weekend__success-text">
                {t("subscribe.success")}
              </p>
            </div>
          ) : (
            <form
              onSubmit={advance}
              className="playium-weekend__form"
              noValidate
            >
              {/* Step indicator — dots clickables (sólo a pasos ya
                  alcanzados) para que el usuario pueda volver atrás. */}
              <div
                className="playium-weekend__steps"
                role="group"
                aria-label="Pasos"
              >
                {STEPS.map((s, i) => (
                  <button
                    key={s.key}
                    type="button"
                    aria-label={`Paso ${i + 1}`}
                    aria-current={i === step ? "step" : undefined}
                    onClick={() => i < step && setStep(i)}
                    disabled={i > step}
                    className={`playium-weekend__step-dot${
                      i === step
                        ? " is-active"
                        : i < step
                        ? " is-done"
                        : ""
                    }`}
                  />
                ))}
              </div>

              {/* Campo activo — la `key` fuerza un re-mount al cambiar de
                  paso, lo que reproduce la animación de entrada CSS. */}
              <label
                key={currentStep.key}
                htmlFor={`weekend-${currentStep.key}`}
                className="playium-weekend__field"
              >
                <span className="sr-only">
                  {t(`subscribe.${currentStep.i18nKey}`)}
                </span>
                <span
                  className="playium-weekend__field-icon"
                  aria-hidden="true"
                >
                  {currentStep.icon}
                </span>
                <input
                  id={`weekend-${currentStep.key}`}
                  type={currentStep.inputType}
                  placeholder={t(`subscribe.${currentStep.i18nKey}`)}
                  value={currentValue}
                  onChange={handleChange}
                  required
                  maxLength={currentStep.maxLength}
                  autoComplete={currentStep.autoComplete}
                  className="playium-weekend__input"
                  autoFocus={step > 0}
                />
              </label>

              <label className="playium-weekend__consent">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                />
                <span>{t("subscribe.consent")}</span>
              </label>

              <div className="playium-weekend__form-actions">
                {step > 0 && (
                  <button
                    type="button"
                    onClick={goBack}
                    className="playium-weekend__back"
                    aria-label={t("subscribe.back")}
                  >
                    <ArrowLeft aria-hidden="true" />
                  </button>
                )}

                <button
                  type="submit"
                  disabled={!canProceed || sending}
                  className="playium-weekend__submit"
                >
                  {isLastStep ? t("subscribe.send") : t("subscribe.continue")}
                  <ArrowRight aria-hidden="true" />
                </button>
              </div>

              <p className="playium-weekend__form-fineprint">
                {t("subscribe.fineprint")}
              </p>
              {/* LDT-706 · Aviso de privacidad con enlace (no se acepta: se informa). */}
              <p className="playium-weekend__form-fineprint">
                <VenueLegalText
                  template={t("subscribe.privacyNotice")}
                  links={{ privacy: { href: venueLegalHref("privacy", i18n.language), label: t("subscribe.privacyLink") } }}
                  linkClassName="playium-checkout__terms-link"
                />
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
